<script setup lang="ts">
import { useListInternalEmployers } from '@swipe4work/api-orval'
import type { ListInternalEmployersInternalEmployerListItemDto } from '@swipe4work/models-orval'
import { useDataTableFilters } from '@/shared/composables/UseDataTableFilters'
import EditInternalEmployerRolesDialog, {
  type EditInternalEmployerRolesDialogData,
} from '@/features/internal-employees/dialogs/EditInternalEmployerRolesDialog.vue'

definePage({
  path: '/interne-medewerkers',
  name: '/settings/InternalEmployees',
  meta: {
    title: 'Interne medewerkers',
    requiresGlobal: true,
  },
})

const PAGE_SIZE = 15
const dialog = useDynamicDialog()

const { searchInput, search, offset, onPage } = useDataTableFilters({
  schema: {},
  defaultFilters: () => ({}),
  defaultPageSize: PAGE_SIZE,
})

const params = computed(() => ({
  search: search.value,
  limit: PAGE_SIZE,
  offset: offset.value,
}))

const { data, isPending, isError, refetch } = useListInternalEmployers(params)

const items = computed(() => data.value?.items ?? [])
const totalCount = computed(() => data.value?.totalCount ?? 0)

const menu = ref()
const selectedItem = ref<ListInternalEmployersInternalEmployerListItemDto | null>(null)

const menuItems = computed(() => [
  {
    label: 'Rollen bewerken',
    icon: 'fa-regular fa-user-shield',
    command: () => openEditRolesDialog(selectedItem.value!),
  },
])

function handleClickRow(event: any, item: ListInternalEmployersInternalEmployerListItemDto) {
  selectedItem.value = item
  menu.value.show(event.originalEvent)
}

function openEditRolesDialog(item: ListInternalEmployersInternalEmployerListItemDto) {
  const dialogData: EditInternalEmployerRolesDialogData = {
    internalEmployerId: item.internalEmployerId,
    displayName: item.displayName ?? item.email,
    currentRoles: item.roles,
    globalPermissions: item.globalPermissions,
  }

  dialog.open(EditInternalEmployerRolesDialog, {
    props: {
      header: 'Rollen bewerken',
      modal: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: dialogData,
    onClose: () => refetch(),
  })
}
</script>

<template>
  <div class="pb-4 space-y-4">
    <Card>
      <template #content>
        <ContextMenu ref="menu" :model="menuItems" @hide="selectedItem = null" />

        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-end">
            <IconField class="max-w-xs flex-1">
              <InputIcon class="fa-regular fa-search" />
              <InputText v-model="searchInput" placeholder="Zoeken op naam of e-mail..." class="w-full" />
            </IconField>
          </div>

          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 8" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van de interne medewerkers.
          </Message>
          <div v-else class="overflow-x-auto">
            <DataTable
              :value="items"
              :lazy="true"
              :first="offset"
              :rows="PAGE_SIZE"
              :total-records="totalCount"
              size="small"
              scrollable
              paginator
              row-hover
              class="w-full"
              @page="onPage"
              @row-click="(e) => handleClickRow(e, e.data)"
            >
              <Column header="Naam / E-mail" style="width: 16rem">
                <template #body="{ data: item }">
                  <div class="flex flex-col overflow-hidden">
                    <span v-if="item.displayName" class="truncate text-sm font-medium">{{ item.displayName }}</span>
                    <span class="truncate text-xs text-surface-400">{{ item.email }}</span>
                  </div>
                </template>
              </Column>

              <Column header="Rollen">
                <template #body="{ data: item }">
                  <div v-if="item.roles.length === 0" class="text-sm text-surface-400">
                    —
                  </div>
                  <div v-else class="flex flex-wrap gap-1">
                    <Tag
                      v-for="role in item.roles"
                      :key="`${role.name}-${role.department}`"
                      :value="`${role.name} · ${role.department}`"
                      severity="secondary"
                    />
                  </div>
                </template>
              </Column>

              <Column header="Globaal" style="width: 8rem">
                <template #body="{ data: item }">
                  <Tag v-if="item.globalPermissions" value="Super-admin" severity="info" />
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>

              <Column header="Laatste login" style="width: 10rem">
                <template #body="{ data: item }">
                  <span v-if="item.lastLoginAt" class="text-sm">
                    {{ useFormatDate(item.lastLoginAt, DateFormat.DateTime) }}
                  </span>
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>

              <Column style="width: 3rem">
                <template #body>
                  <i class="fa-regular fa-chevron-right text-surface-400" />
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
